import * as Dialog from '@radix-ui/react-dialog';
import { type FormEvent, type ReactNode, useState } from 'react';
import { Button, IconButton } from './button';
import { cn } from './cn';
import { Alert } from './feedback';
import { useUiStrings } from './strings';

interface DialogFrameProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  size?: 'md' | 'lg';
  children: ReactNode;
}

/** The one modal frame: closes with ×, Cancel, Escape and a click on the backdrop (FR-NAV-009). */
function DialogFrame({ open, onOpenChange, title, description, size = 'md', children }: DialogFrameProps) {
  const s = useUiStrings();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          data-testid="dialog-overlay"
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[1px]"
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-[8vh] z-50 max-h-[84vh] w-[calc(100vw-2rem)] -translate-x-1/2 overflow-y-auto',
            'rounded-card border border-border bg-surface shadow-xl',
            size === 'lg' ? 'max-w-2xl' : 'max-w-md',
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
            <div>
              <Dialog.Title className="text-lg font-semibold">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-sm text-ink-muted">{description}</Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <IconButton icon="x" label={s.close} />
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Dialog that shows a panel (e.g. a project's notes, FR-DASH-011) — no form footer. */
export function PanelDialog(props: DialogFrameProps) {
  return (
    <DialogFrame size="lg" {...props}>
      <div className="px-5 py-4">{props.children}</div>
    </DialogFrame>
  );
}

export interface FormDialogProps extends Omit<DialogFrameProps, 'children'> {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  submitLabel?: string;
  submitting?: boolean;
  /** Form-level error (not tied to one field). */
  error?: string | null;
  children: ReactNode;
}

/** Create/edit dialog — every entity form is this plus its fields. */
export function FormDialog({
  onSubmit,
  submitLabel,
  submitting,
  error,
  children,
  ...frame
}: FormDialogProps) {
  const s = useUiStrings();
  return (
    <DialogFrame {...frame}>
      <form noValidate onSubmit={onSubmit}>
        <div className="flex flex-col gap-4 px-5 py-4">
          {error && <Alert tone="danger">{error}</Alert>}
          {children}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="secondary" onClick={() => frame.onOpenChange(false)}>
            {s.cancel}
          </Button>
          <Button type="submit" loading={submitting}>
            {submitLabel ?? s.save}
          </Button>
        </div>
      </form>
    </DialogFrame>
  );
}

export interface ConfirmDialogProps extends Omit<DialogFrameProps, 'children'> {
  message: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  busy?: boolean;
  error?: string | null;
  /** When set, the confirm button unlocks only after typing this word (e.g. DELETE). */
  confirmWord?: string;
}

/** Destructive or important confirmation (delete, remove member, revoke). */
export function ConfirmDialog({
  message,
  confirmLabel,
  onConfirm,
  busy,
  error,
  confirmWord,
  ...frame
}: ConfirmDialogProps) {
  return (
    <DialogFrame {...frame}>
      {/* Mounted only while open, so a typed confirmation word never survives to the next dialog. */}
      <ConfirmBody
        message={message}
        confirmLabel={confirmLabel}
        onConfirm={onConfirm}
        onCancel={() => frame.onOpenChange(false)}
        busy={busy}
        error={error}
        confirmWord={confirmWord}
      />
    </DialogFrame>
  );
}

function ConfirmBody({
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  busy,
  error,
  confirmWord,
}: Pick<ConfirmDialogProps, 'message' | 'confirmLabel' | 'onConfirm' | 'busy' | 'error' | 'confirmWord'> & {
  onCancel: () => void;
}) {
  const s = useUiStrings();
  const [typed, setTyped] = useState('');
  const locked = confirmWord !== undefined && typed !== confirmWord;
  return (
    <>
      <div className="flex flex-col gap-4 px-5 py-4 text-sm">
        {error && <Alert tone="danger">{error}</Alert>}
        <div>{message}</div>
        {confirmWord && (
          <label className="flex flex-col gap-1.5">
            <span className="font-medium">{s.typeToConfirm(confirmWord)}</span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              className="h-10 rounded-lg border border-border px-3 focus:border-brand-500 focus:outline-none"
            />
          </label>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
        <Button variant="secondary" onClick={onCancel}>
          {s.cancel}
        </Button>
        <Button variant="danger" disabled={locked} loading={busy} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </>
  );
}
